import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import IKUtils from '@/model/utils/IkUtils/Utils'

export const InputType = {
  SingleText: 'SingleText',
  TextArea: 'TextArea',
  Number: 'Number',
  Selection: 'Selection'
}

// Define a dialog state interface
// Each dialog in the stack will have these properties
const createDialogState = () => ({
  show: false,
  schemas: [],
  oldObject: null,
  resolve: null,
  loading: false,
  title: '',
  subtitle: '',
  isNewMode: true
})

export const useBaseFormDialogStore = defineStore('baseFormDialog', () => {
  // Stack of dialog states to support nested dialogs
  const dialogStack = ref([createDialogState()])

  // Current dialog is always the top of the stack
  const currentDialog = computed(() => {
    return dialogStack.value[dialogStack.value.length - 1]
  })

  // No longer need backward compatibility computed properties

  async function waitFor (action) {
    currentDialog.value.loading = true
    try {
      await action()
      // Close the current dialog
      closeCurrentDialog(null)
    } catch (e) {
      console.log(e, 'error handle')
    }
    currentDialog.value.loading = false
  }

  async function editItem (schema, object = null) {
    // Create a new dialog state and push it to the stack
    const newDialog = createDialogState()
    newDialog.show = true
    newDialog.loading = true
    newDialog.oldObject = object
    newDialog.isNewMode = object === null

    dialogStack.value.push(newDialog)

    const { schemas, title, subtitle } = await schema

    // If existing object provided, normalize date fields to YYYY-MM-DD for DateSelector components
    try {
      if (object) {
        const getByPath = (obj, path) => {
          if (!obj || !path) return undefined
          return path.split('.').reduce((acc, k) => (acc != null ? acc[k] : undefined), obj)
        }
        const setByPath = (obj, path, value) => {
          if (!obj || !path) return
          const parts = path.split('.')
          const last = parts.pop()
          let cur = obj
          for (const p of parts) {
            if (cur[p] == null || typeof cur[p] !== 'object') cur[p] = {}
            cur = cur[p]
          }
          cur[last] = value
        }

        const dateKeys = (schemas || [])
          .filter(s => s && s.component && s.component.name === 'DateSelector' && s.key)
          .map(s => s.key)

        if (dateKeys.length > 0) {
          const copied = IKUtils.deepCopy(object)
          dateKeys.forEach(k => {
            const v = getByPath(copied, k)
            if (v !== undefined && v !== null && v !== '') {
              const d = dayjs(v)
              if (d.isValid()) {
                setByPath(copied, k, d.format('YYYY-MM-DD'))
              }
            }
          })
          newDialog.oldObject = copied
        }
      }
    } catch (e) {
      // Fail silently; do not block opening the dialog
      // console.warn('Date normalization failed', e)
    }

    return new Promise(_resolve => {
      // Update the new dialog with schema data
      newDialog.schemas = schemas
      newDialog.title = title
      newDialog.subtitle = subtitle
      newDialog.loading = false
      newDialog.resolve = _resolve
    })
  }

  // Close the current dialog and remove it from the stack
  function closeCurrentDialog (result = null) {
    if (dialogStack.value.length > 0) {
      const dialog = dialogStack.value.pop()

      // Resolve the promise if it exists
      if (dialog.resolve) {
        dialog.resolve(result)
      }

      // If there are no more dialogs, create an initial state
      if (dialogStack.value.length === 0) {
        dialogStack.value.push(createDialogState())
      }
    }
  }

  return {
    // Dialog stack properties
    dialogStack,
    currentDialog,

    // Methods
    editItem,
    waitFor,
    closeCurrentDialog
  }
})
